/**
 * Who owns Mountainsnow AS, as a ring — 31 Aug 2026.
 *
 * A shareholder reading 144 rows cannot see the shape of the company from them,
 * which is the one thing a cap table is for. So: the eight largest holders, the
 * person reading it, and everybody else as a single slice.
 *
 * The reader's own slice is always drawn even when they are the 90th largest —
 * being able to find yourself is the reason this is on their screen and not on
 * ours. It is pulled out of "the rest" rather than added on top, so the arcs
 * still sum to the whole company.
 *
 * No chart library. It is one ring of `stroke-dasharray` arcs, which is less
 * code than the wrapper around a dependency would be.
 */
import { nf } from '@/shared/util/numbers'

export interface PieRow {
  name: string
  shares: number
}

/** Eight named holders, the reader, then everybody else. */
const TOP_N = 8

/**
 * Distinct enough to tell apart at 14px, and none of them the greys the rest of
 * the page is built from. "The rest" is deliberately the flattest colour here:
 * it is the one slice that is not a holder.
 */
const COLOURS = [
  '#000b38',
  '#3456da',
  '#0f9b8e',
  '#e8a52f',
  '#7b5ea7',
  '#d9534f',
  '#2f8fd8',
  '#59a14f',
  '#b07aa1',
]
const REST = '#c9d2e0'

const R = 56
const STROKE = 26
const C = 2 * Math.PI * R

export function CapTablePie({
  rows,
  total,
  meName,
}: {
  rows: PieRow[]
  total: number
  /** The reader's own row, so it is never folded into "the rest". */
  meName?: string
}) {
  if (!rows.length || total <= 0) return null

  // `rows` arrives sorted largest first from the generated cap table, so the
  // top eight are just the first eight. Sorting again here would be a second
  // opinion about the same question.
  const named = rows.slice(0, TOP_N)
  const mine = meName ? rows.find((r) => r.name === meName) : undefined
  if (mine && !named.includes(mine)) named.push(mine)

  const accounted = named.reduce((n, r) => n + r.shares, 0)
  const rest = total - accounted

  const slices = [
    ...named.map((r, i) => ({
      key: r.name,
      label: r.name,
      shares: r.shares,
      colour: COLOURS[i % COLOURS.length],
      isMe: !!meName && r.name === meName,
    })),
    // Only when there is anything left. With nine holders or fewer, a slice
    // labelled "the rest" of nothing is a lie about the register.
    ...(rest > 0
      ? [{ key: '__rest', label: `The other ${nf(rows.length - named.length)} holders`, shares: rest, colour: REST, isMe: false }]
      : []),
  ]

  let offset = 0
  const arcs = slices.map((s, i) => {
    const len = (s.shares / total) * C
    // Keyed by position as well as name: two holders may be recorded under
    // the same name, and React only complains about the second one.
    const arc = { ...s, key: `${s.key}-${i}`, len, offset, pct: (s.shares / total) * 100 }
    offset += len
    return arc
  })

  return (
    <div className="cappie">
      <svg
        className="cappie__ring"
        viewBox="0 0 160 160"
        role="img"
        aria-label={`Ownership split across ${nf(rows.length)} holders. ${arcs
          .map((a) => `${a.label} ${a.pct.toFixed(1)}%`)
          .join(', ')}.`}
      >
        {/* Rotated so the first and largest slice starts at twelve o'clock,
            which is where the eye starts looking for it. */}
        <g transform="rotate(-90 80 80)">
          {arcs.map((a) => (
            <circle
              key={a.key}
              cx="80"
              cy="80"
              r={R}
              fill="none"
              stroke={a.colour}
              strokeWidth={a.isMe ? STROKE + 6 : STROKE}
              strokeDasharray={`${a.len} ${C - a.len}`}
              strokeDashoffset={-a.offset}
            />
          ))}
        </g>
        {/* The reader's own figure in the hole, because it is the number they
            opened the page for. */}
        {mine && (
          <>
            <text className="cappie__big" x="80" y="78" textAnchor="middle">
              {((mine.shares / total) * 100).toFixed(2).replace('.', ',')}%
            </text>
            <text className="cappie__small" x="80" y="94" textAnchor="middle">
              yours
            </text>
          </>
        )}
      </svg>

      <ul className="cappie__legend">
        {arcs.map((a) => (
          <li key={a.key} className={a.isMe ? 'cappie__item cappie__item--me' : 'cappie__item'}>
            <span className="cappie__dot" style={{ background: a.colour }} aria-hidden="true" />
            <span className="cappie__name">
              {a.label}
              {a.isMe && <span className="cappie__you"> · you</span>}
            </span>
            <span className="cappie__pct">{a.pct.toFixed(2).replace('.', ',')}%</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
